import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import {
  ChartLineUp, CurrencyEur, Users, CrownSimple, Clock, ForkKnife,
  ArrowLeft, EnvelopeSimple, ArrowUp, ChatCircleDots, Check,
} from "@phosphor-icons/react";
import CookiesManager from "../components/CookiesManager";
import GiftProManager from "../components/GiftProManager";

export default function Admin() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [metrics, setMetrics] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [triggering, setTriggering] = useState(false);

  const load = async () => {
    try {
      const [m, t] = await Promise.all([
        api.get("/admin/metrics"),
        api.get("/admin/tickets?status_filter=open"),
      ]);
      setMetrics(m.data);
      setTickets(t.data.tickets || []);
    } catch (e) {
      setError(e.response?.status === 403 ? "Solo administradores" : "Error al cargar métricas");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const resolveTicket = async (tid) => {
    await api.post(`/admin/tickets/${tid}/resolve`);
    setTickets((ts) => ts.filter((t) => t.id !== tid));
  };

  const triggerReminders = async () => {
    setTriggering(true);
    try {
      await api.post("/admin/trigger-reminders");
    } catch {}
    setTriggering(false);
  };

  if (loading) return <div className="text-center py-20 text-[hsl(var(--muted-foreground))]">Cargando métricas…</div>;
  if (error) return <div className="text-center py-20 text-[hsl(var(--destructive))]">{error}</div>;
  if (!metrics) return null;

  const eur = (n) => new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(n);

  const maxDaily = Math.max(1, ...metrics.daily_extractions.map((d) => d.count));

  return (
    <div className="space-y-10" data-testid="admin-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="eyebrow mb-2 text-[hsl(var(--primary))]">panel admin</p>
          <h1 className="font-serif text-4xl sm:text-5xl tracking-tight">Cocina en cifras</h1>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={triggerReminders} disabled={triggering} data-testid="trigger-reminders-btn" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[hsl(var(--border))] text-sm font-medium btn-press">
            <EnvelopeSimple size={16} /> {triggering ? "Enviando…" : "Disparar recordatorios"}
          </button>
        </div>
      </div>

      {/* KPI cards */}
      <section className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi icon={Users} label="Usuarios totales" value={metrics.users.total} sub={`${metrics.users.active_30d} activos · 30d`} />
        <Kpi icon={CrownSimple} label="Suscriptores Pro" value={metrics.users.pro} sub={`${metrics.users.free} en free`} accent />
        <Kpi icon={CurrencyEur} label="Ingresos totales" value={eur(metrics.revenue.total)} sub={`Mensual ${eur(metrics.revenue.monthly_plan)} · Anual ${eur(metrics.revenue.yearly_plan)}`} />
        <Kpi icon={ChatCircleDots} label="Tickets abiertos" value={tickets.length} sub={tickets.length === 0 ? "Todo en orden" : "Necesitan respuesta"} />
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 rounded-3xl bg-white border border-[hsl(var(--border))] p-6">
          <div className="flex items-center justify-between mb-5">
            <p className="eyebrow">extracciones · últimos 14 días</p>
            <span className="text-xs text-[hsl(var(--muted-foreground))]">Total: {metrics.recipes.total} · 30d: {metrics.recipes.last_30_days}</span>
          </div>
          <div className="flex items-end gap-2 h-40">
            {metrics.daily_extractions.map((d) => (
              <div key={d.date} className="flex-1 flex flex-col items-center gap-1 group">
                <div
                  className="w-full bg-[hsl(var(--primary))]/15 hover:bg-[hsl(var(--primary))]/30 rounded-t transition-colors relative"
                  style={{ height: `${(d.count / maxDaily) * 100}%`, minHeight: "2px" }}
                  title={`${d.count} el ${d.date}`}
                >
                  {d.count > 0 && (
                    <span className="absolute -top-5 left-1/2 -translate-x-1/2 text-[10px] font-semibold opacity-0 group-hover:opacity-100">{d.count}</span>
                  )}
                </div>
                <span className="text-[9px] text-[hsl(var(--muted-foreground))]">{d.date.slice(8, 10)}/{d.date.slice(5, 7)}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-3xl bg-white border border-[hsl(var(--border))] p-6">
          <p className="eyebrow mb-5">vencimientos próximos</p>
          <Row label="Próximos 7 días" value={metrics.expirations.next_7_days} icon={Clock} />
          <Row label="Próximos 30 días" value={metrics.expirations.next_30_days} icon={Clock} />
          <p className="mt-4 text-xs text-[hsl(var(--muted-foreground))] leading-relaxed">
            Se envía un email automático 7 días antes del vencimiento si Resend está configurado.
          </p>
        </div>
      </section>

      {/* Tickets */}
      <section className="rounded-3xl bg-white border border-[hsl(var(--border))] p-6">
        <p className="eyebrow mb-5">tickets de soporte abiertos</p>
        {tickets.length === 0 ? (
          <p className="text-sm text-[hsl(var(--muted-foreground))]">No hay tickets sin resolver.</p>
        ) : (
          <div className="space-y-3">
            {tickets.map((t) => (
              <div key={t.id} data-testid={`ticket-${t.id}`} className="rounded-2xl border border-[hsl(var(--border))] p-4 flex flex-col sm:flex-row sm:items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <span className="font-medium text-sm">{t.user_name}</span>
                    <a href={`mailto:${t.user_email}?subject=Re: Tu mensaje en MIZETA`} className="text-xs text-[hsl(var(--primary))] underline">{t.user_email}</a>
                    <span className="text-[10px] text-[hsl(var(--muted-foreground))]">{new Date(t.created_at).toLocaleString("es-ES", { dateStyle: "short", timeStyle: "short" })}</span>
                  </div>
                  <p className="text-sm whitespace-pre-wrap leading-relaxed">{t.message}</p>
                </div>
                <button
                  onClick={() => resolveTicket(t.id)}
                  data-testid={`resolve-${t.id}`}
                  className="shrink-0 inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[hsl(var(--secondary))]/10 text-[hsl(var(--secondary))] text-xs font-semibold btn-press hover:bg-[hsl(var(--secondary))]/20"
                >
                  <Check size={14} /> Marcar resuelto
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      <CookiesManager />

      <GiftProManager />

      <section className="rounded-3xl bg-white border border-[hsl(var(--border))] p-6">
        <p className="eyebrow mb-5">pagos recientes</p>        {metrics.recent_payments.length === 0 ? (
          <p className="text-sm text-[hsl(var(--muted-foreground))]">Aún no hay pagos.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-[hsl(var(--muted-foreground))] text-xs uppercase tracking-wider">
                <tr>
                  <th className="py-2 pr-4">Email</th>
                  <th className="py-2 pr-4">Plan</th>
                  <th className="py-2 pr-4 text-right">Importe</th>
                  <th className="py-2 pr-4">Fecha</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[hsl(var(--border))]">
                {metrics.recent_payments.map((p, i) => (
                  <tr key={i} data-testid={`payment-row-${i}`} className="hover:bg-[hsl(var(--background))]">
                    <td className="py-3 pr-4 font-medium">{p.email}</td>
                    <td className="py-3 pr-4">
                      <span className={`px-2 py-0.5 rounded-full text-xs ${p.plan_id === "pro_yearly" ? "bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]" : "bg-[hsl(var(--secondary))]/10 text-[hsl(var(--secondary))]"}`}>
                        {p.plan_id === "pro_yearly" ? "Anual" : "Mensual"}
                      </span>
                    </td>
                    <td className="py-3 pr-4 text-right font-mono">{eur(p.amount)}</td>
                    <td className="py-3 pr-4 text-[hsl(var(--muted-foreground))] text-xs">
                      {p.completed_at ? new Date(p.completed_at).toLocaleString("es-ES", { dateStyle: "short", timeStyle: "short" }) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, sub, accent }) {
  return (
    <div className={`rounded-3xl p-5 border ${accent ? "bg-[hsl(var(--foreground))] text-white border-[hsl(var(--foreground))]" : "bg-white border-[hsl(var(--border))]"}`}>
      <div className="flex items-center justify-between">
        <Icon size={18} className={accent ? "text-[hsl(var(--primary))]" : "text-[hsl(var(--muted-foreground))]"} weight="duotone" />
        <ArrowUp size={14} className="opacity-40" />
      </div>
      <p className="mt-3 font-serif text-3xl leading-none">{value}</p>
      <p className={`mt-1 text-xs ${accent ? "text-white/60" : "text-[hsl(var(--muted-foreground))]"}`}>{label}</p>
      {sub && <p className={`mt-2 text-[11px] ${accent ? "text-white/50" : "text-[hsl(var(--muted-foreground))]"}`}>{sub}</p>}
    </div>
  );
}

function Row({ label, value, icon: Icon }) {
  return (
    <div className="flex items-center justify-between py-3 border-b border-[hsl(var(--border))] last:border-0">
      <span className="inline-flex items-center gap-2 text-sm">
        <Icon size={14} className="text-[hsl(var(--muted-foreground))]" /> {label}
      </span>
      <span className="font-serif text-xl">{value}</span>
    </div>
  );
}
